import React, { useCallback, useEffect, useState } from "react";
import { calculateCropRect, cropRectToImagePosition } from "../utils";

const useDrag = (ref, startingPosition, options) => {
  const [isDragging, setIsDragging] = useState(false);

  const [dragInfo, setDragInfo] = useState(() => {
    console.log("Options", startingPosition, options);
    let size = {
      width: startingPosition.width,
      height: startingPosition.height,
    };
    const opts = {
      isDragging: false,
      origin: null,
      adaption: options.adaption || "fill-in",
      translation: {
        ...startingPosition,
      },
      lastTranslation: {
        ...startingPosition,
        ...size,
      },
      completeTranslation: null,
      cover: true,
      containerSize: { width: 200, height: 300 },
      size,
      originalSize: size,
      multiTouch: false,
      rotation: 0,
      ...options,
    };
    console.log("Opts", opts);
    return opts;
  });

  useEffect(() => {
    if (dragInfo.onUpdate && isDragging) {
      console.log("onHandleStuff: On update....", dragInfo.translation);
      dragInfo.onUpdate(dragInfo.translation);
    }
  }, [dragInfo]);

  useEffect(() => {
    console.log("Resetting options");
    let size = {
      width: startingPosition.width,
      height: startingPosition.height,
    };
    setDragInfo((dragInfo) => {
      return {
        ...dragInfo,
        lastTranslation: {
          ...startingPosition,
          ...size,
        },
        translation: {
          ...startingPosition,
          ...size,
        },
        size: size,
        originalSize: size,
        containerSize: options.containerSize,
      };
    });
  }, [options, startingPosition]);

  useEffect(() => {
    console.log("Complete translation", dragInfo.completeTranslation);
    if (dragInfo.completeTranslation) {
      if (dragInfo.onChange) {
        dragInfo.onChange(dragInfo.completeTranslation);
      }
    }
  }, [dragInfo.completeTranslation]);

  useEffect(() => {
    console.log("binding touch events!", ref);
    // bind:
    if (ref.current) {
      ref.current.addEventListener("touchstart", (e) => {
        console.log("On touch start", e);
        handleMouseDown(e);
      });

      ref.current.addEventListener("touchend", (e) => {
        console.log("On touch end");
        handleMouseUp(e);
      });

      ref.current.addEventListener("touchmove", (e) => {
        handleMouseMove(e);
      });

      ref.current.addEventListener("wheel", (e) => {
        handleMouseWheel(e);
      });

      return () => {
        // I guess this is garbage collected and never ran?
        if (ref.current) {
          ref.current.removeEventListener("touchmove", handleMouseMove);
          ref.current.removeEventListener("touchstart", handleMouseDown);
          ref.current.removeEventListener("touchstart", handleMouseDown);
          ref.current.removeEventListener("wheel", handleMouseDown);
        }
      };
    }
  }, []);

  const getEventPositions = (e) => {
    const br = ref.current.parentNode.getBoundingClientRect();
    const scale = br.width / ref.current.parentNode.offsetWidth;

    console.log(
      "Getting scale:",
      ref.current.parentNode,
      ref.current.parentNode.offsetWidth,
      scale,
    );

    const getCoords = (x, y) => {
      console.log("Rotation is " + dragInfo.rotation);
      if (!dragInfo.rotation) {
        return { x, y };
      }
      if (dragInfo.rotation == 270) {
        return { x: -y, y: x };
      } else if (dragInfo.rotation == 90) {
        return { x: y, y: -x };
      } else if (dragInfo.rotation == 180) {
        return { x: -x, y: -y };
      }
      return { x, y };
    };

    if (typeof e.clientX != "undefined") {
      console.log("Using client x", e.clientX);
      return {
        multiTouch: false,
        positions: [
          {
            ...getCoords(e.clientX, e.clientY),
          },
        ],
      };
    }

    if (typeof e.targetTouches != "undefined") {
      console.log("Using target touches", e.clientX);
      return {
        multiTouch: e.targetTouches.length > 1,
        positions: Array.from(e.targetTouches).map((touch) => {
          const x = (touch.clientX - br.left) / scale;
          const y = (touch.clientY - br.top) / scale;
          return {
            id: touch.identifier,
            ...getCoords(x, y),
          };
        }),
      };
    }

    if (typeof e.touches != "undefined") {
      console.log("Using touches touches", e.clientX);
      return {
        multiTouch: e.touches.length > 1,
        positions: Array.from(e.touches).map((touch) => {
          const x = (touch.clientX - br.left) / scale;
          const y = (touch.clientY - br.top) / scale;
          return {
            id: touch.identifier,
            ...getCoords(x, y),
          };
        }),
      };
    }

    if (typeof e.changedTouches && e.changedTouches.length > 0) {
      return {
        multiTouch: e.changedTouches.length > 1,
        positions: Array.from(e.changedTouches).map((touch) => {
          const x = (touch.clientX - br.left) / scale;
          const y = (touch.clientY - br.top) / scale;
          return {
            id: touch.identifier,
            ...getCoords(x, y),
          };
        }),
      };
    }
  };

  const handleMouseWheel = useCallback((e) => {
    // Disable page scroll:
    e.preventDefault();

    e.deltaY < 0 ? zoomIn(e) : zoomOut(e);
    handleMouseUp();
  }, []);

  const zoomIn = useCallback((e) => {
    setDragInfo((dragInfo) => {
      const { lastTranslation, originalSize } = dragInfo;
      // Zoom 10%?
      const newWidth = lastTranslation.width * 1.1;
      const currDiff = originalSize.width / lastTranslation.width;
      const newDiff = newWidth / lastTranslation.width;
      const scaleChange = newDiff - currDiff;

      // Console.log
      const pos = getEventPositions(e);

      const newTranslation = {
        x: lastTranslation.x - pos.positions[0].x * scaleChange,
        y: lastTranslation.y - pos.positions[0].y * scaleChange,
        width: lastTranslation.width * newDiff,
        height: lastTranslation.height * newDiff,
      };

      return {
        ...dragInfo,
        translation: newTranslation,
        lastTranslation: newTranslation,
      };
    });
  }, []);

  const zoomOut = useCallback((e) => {
    setDragInfo((dragInfo) => {
      const { lastTranslation, originalSize } = dragInfo;
      // Zoom 10%?
      const newWidth = lastTranslation.width * 0.9;
      const currDiff = originalSize.width / lastTranslation.width;
      const newDiff = newWidth / lastTranslation.width;
      const scaleChange = newDiff - currDiff;

      // Console.log
      const pos = getEventPositions(e);
      console.log(
        "The mouse position",
        pos,
        currDiff,
        newDiff,
        lastTranslation,
        scaleChange,
      );

      const newTranslation = {
        x: lastTranslation.x - pos.positions[0].x * scaleChange,
        y: lastTranslation.y - pos.positions[0].y * scaleChange,
        width: lastTranslation.width * newDiff,
        height: lastTranslation.height * newDiff,
      };

      return {
        ...dragInfo,
        translation: newTranslation,
        lastTranslation: newTranslation,
      };
    });
  }, []);

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    const origin = getEventPositions(e);

    // On touch start will only get one event:
    // At most 2 fingers:
    if (origin.positions.length > 2) {
      return;
    }

    console.log("Event down!!!", origin.positions[0]);

    setIsDragging(true);

    setDragInfo((dragInfo) => {
      const { lastTranslation } = dragInfo;

      let centerPointStartX = 0;
      let centerPointStartY = 0;
      let percentageOfImageAtPinchPointX = 0;
      let percentageOfImageAtPinchPointY = 0;

      if (origin.positions.length > 1) {
        centerPointStartX =
          (origin.positions[0].x + origin.positions[1].x) / 2.0;
        centerPointStartY =
          (origin.positions[0].y + origin.positions[1].y) / 2.0;

        percentageOfImageAtPinchPointX =
          (centerPointStartX - lastTranslation.x) / lastTranslation.width;
        percentageOfImageAtPinchPointY =
          (centerPointStartY - lastTranslation.y) / lastTranslation.height;
      }

      // We need to set it as it can be multi touch
      return {
        ...dragInfo,
        origin: origin,
        translation: dragInfo.lastTranslation,
        percentageOfImageAtPinchPointY,
        percentageOfImageAtPinchPointX,
        centerPointStartX,
        centerPointStartY,
      };
    });
  }, []);

  const handleMouseMove = useCallback((e) => {
    e.preventDefault();
    const positions = getEventPositions(e);

    // Hackity hack!
    setDragInfo((dragInfo) => {
      const {
        originalSize,
        origin,
        lastTranslation,
        percentageOfImageAtPinchPointX,
        percentageOfImageAtPinchPointY,
        centerPointStartX,
        centerPointStartY,
      } = dragInfo;

      if (!origin) {
        return dragInfo;
      }

      if (
        !e.touches ||
        !(e.touches.length == 2 && e.targetTouches.length == 2)
      ) {
        const { x: clientX, y: clientY } = positions.positions[0];

        let x = clientX - (origin.positions[0].x - lastTranslation.x);
        let y = clientY - (origin.positions[0].y - lastTranslation.y);

        /*
        if (dragInfo.rotation == 270) {
          const tmpx = x;
          x = -y;
          y = tmpx;
        }
         */
        console.log("Were moving", x, y, origin);

        // Make sure we have a mouse down event:
        // if we have a touch event, we must have a start event:
        if (e.targetTouches) {
          const found = origin.positions.find(
            (pos) => pos.id === e.targetTouches[0].identifier,
          );
          if (!found) {
            return dragInfo;
          }
        }

        return {
          ...dragInfo,
          translation: {
            ...dragInfo.translation,
            x,
            y,
          },
        };
      } else if (e.targetTouches.length == 2 && e.changedTouches.length == 2) {
        var point1 = -1,
          point2 = -1;
        for (var i = 0; i < origin.positions.length; i++) {
          if (origin.positions[i].id == e.targetTouches[0].identifier)
            point1 = i;
          if (origin.positions[i].id == e.targetTouches[1].identifier)
            point2 = i;
        }

        if (point1 < 0 || point2 < 0) {
          console.log("Rage quit?!");
          // Reset our orin:
          setDragInfo((dragInfo) => ({
            ...dragInfo,
            origin: null,
          }));
          return;
        }

        const origDistance = Math.hypot(
          origin.positions[point2].x - origin.positions[point1].x,
          origin.positions[point2].y - origin.positions[point1].y,
        );

        console.log("Distance of drag", origDistance);
        const currentDistance = Math.hypot(
          positions.positions[point2].x - positions.positions[point1].x,
          positions.positions[point2].y - positions.positions[point1].y,
        );

        const pinchRatio = currentDistance / origDistance;
        const currentZoom =
          (originalSize.width / lastTranslation.width) * pinchRatio;

        let width = lastTranslation.width * currentZoom;
        let height = lastTranslation.height * currentZoom;

        // Get the point between the two touches, relative to upper-left corner of image
        let centerPointEndX =
          (positions.positions[point1].x + positions.positions[point2].x) / 2;
        let centerPointEndY =
          (positions.positions[point1].y + positions.positions[point2].y) / 2;

        // This is the translation due to pinch-zooming
        const translateFromZoomingX =
          (lastTranslation.width - width) * percentageOfImageAtPinchPointX;
        const translateFromZoomingY =
          (lastTranslation.height - height) * percentageOfImageAtPinchPointY;

        // And this is the translation due to translation of the centerpoint between the two fingers
        const translateFromTranslatingX = centerPointEndX - centerPointStartX;
        const translateFromTranslatingY = centerPointEndY - centerPointStartY;

        // Total translation is from two components: (1) changing height and width from zooming and (2) from the two fingers translating in unity
        const translateTotalX =
          translateFromZoomingX + translateFromTranslatingX;
        const translateTotalY =
          translateFromZoomingY + translateFromTranslatingY;

        // the new offset is the old/current one plus the total translation component
        const x = lastTranslation.x + translateTotalX;
        const y = lastTranslation.y + translateTotalY;

        const pos = {
          ...lastTranslation,
          y,
          x,
          width,
          height,
        };

        return {
          ...dragInfo,
          translation: pos,
        };
      }
      return dragInfo;
    });
  }, []);

  const handleMouseUp = useCallback((e) => {
    console.log("touchEnd: Mouse up!");
    // don't save till we have 0 touches:
    if (e && e.targetTouches && e.targetTouches.length > 0) {
      // If we have more than 0 touches after mouse up, reset zoom:
      setDragInfo((dragInfo) => {
        return {
          ...dragInfo,
          origin: null,
        };
      });
      return;
    }

    console.log("touchEnd: Mouse up!");
    setIsDragging(false);

    setDragInfo((dragInfo) => {
      const { origin, translation } = dragInfo;

      console.log("touchEnd: Yey?");
      let pos;
      console.log("Adaption", dragInfo.adaption);
      if (dragInfo.adaption == "fill-in") {
        console.log("The_Drag info", dragInfo);
        pos = getFillInPosition(
          dragInfo.size,
          dragInfo.containerSize,
          translation,
          dragInfo.originalSize,
        );
        console.log("fillInPosition Input - output", translation, pos);
      } else {
        console.log("The_Drag info", dragInfo);
        pos = getFitInPosition(
          dragInfo.size,
          dragInfo.containerSize,
          translation,
          dragInfo.originalSize,
        );
      }

      console.log("touchEnd - Final position:", pos);

      console.log("Image crop position useDrag", pos);
      return {
        ...dragInfo,
        lastTranslation: pos,
        translation: pos,
        completeTranslation: pos,
        origin: null,
      };
    });
  }, []);

  const picturePosition = dragInfo.translation;

  return {
    isDragging: isDragging,
    picturePosition,
    eventHandlers: {
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseMove: handleMouseMove,
      //onTouchStart: handleMouseDown,
      //onTouchEnd: handleMouseUp,
      //onTouchMove: handleMouseMove,
    },
  };
};

export default useDrag;

const getFitInPosition = (size, containerSize, translation, originalSize) => {
  let { y, x, width, height } = translation;

  // Figure out what size to snap to:
  const destRatio = Math.abs(containerSize.width / containerSize.height);
  const imageRatio = Math.abs(size.width / size.height);

  if (destRatio < imageRatio) {
    console.log("touchEnd: Use this side", width, containerSize.width);

    if (width < containerSize.width) {
      console.log("touchEnd: We need to scale it!");
      // Needed scale ratio:
      const scaleRatio = containerSize.width / originalSize.width;
      width = originalSize.width * scaleRatio;
      height = originalSize.height * scaleRatio;
    }

    if (x > 0) {
      x = 0;
    } else if (x + width < containerSize.width) {
      x = containerSize.width - width;
    }

    if (y < 0 && y + height > containerSize.height) {
      // This is ok:
      console.log("touchEnd: Y This is ok!");
    } else if (y > 0 && height > containerSize.height) {
      y = 0;
      console.log("touchEnd: Y is 0");
    } else if (
      y < 0 &&
      y + height < containerSize.height &&
      height >= containerSize.height
    ) {
      y = containerSize.height - height;
      console.log("touchEnd: Y is containerHeight - height");
    } else {
      console.log("Half and half!");
      y = containerSize.height / 2 - height / 2;
      console.log("touchEnd: Y is less than height");
    }
  } else {
    console.log("touchEnd: Use that size!", x, y, width, height);
    // It's still to small?
    if (height < containerSize.height) {
      console.log("touchEnd: We need to scale it!");
      // Needed scale ratio:
      const scaleRatio = containerSize.height / originalSize.height;
      height = originalSize.height * scaleRatio;
      width = originalSize.width * scaleRatio;
    }

    if (y > 0) {
      y = 0;
    } else if (y + height < containerSize.height) {
      y = containerSize.height - height;
    }

    if (x < 0 && x + width > containerSize.width) {
      // This is ok:
      console.log("touchEnd: This is ok!");
    } else if (x > 0 && width > containerSize.width) {
      x = 0;
    } else if (
      x < 0 &&
      x + width < containerSize.width &&
      width >= containerSize.width
    ) {
      console.log("touchEnd:  Container size - width");
      x = containerSize.width - width;
    } else {
      console.log("touchEnd: Half and half!");
      x = containerSize.width / 2 - width / 2;
    }
  }
  return { x, y, width, height };
};

const getFillInPosition = (size, containerSize, translation, originalSize) => {
  console.log("getFillInPosition", translation, originalSize, containerSize);

  // Check if x/y is off:
  let x = translation.x <= 0 ? translation.x : 0;
  let y = translation.y <= 0 ? translation.y : 0;

  let width = translation.width;
  if (x + translation.width < containerSize.width) {
    x = containerSize.width - translation.width;
  }
  let height = translation.height;
  console.log(
    "fillInPosition Y value",
    y + translation.height,
    containerSize.height,
  );
  if (y + translation.height < containerSize.height) {
    y = containerSize.height - translation.height;
  }

  const cr = {
    x,
    y,
    width,
    height,
  };

  const cropRect = calculateCropRect(cr, originalSize, containerSize);
  console.log(
    "fillInPosition - crop rect",
    cr,
    translation,
    containerSize,
    cropRect,
    originalSize,
  );

  const outputPosition = cropRectToImagePosition(
    cropRect,
    originalSize,
    containerSize,
    "fill-in",
  );
  console.log("fillInPosition image position", outputPosition);
  return outputPosition;
};
